<script>
  import { getContext } from 'svelte'
  import { fly } from 'svelte/transition'
  import Tabs from './components/Tabs.svelte'
  import BlankLayouts from './components/BlankLayouts.svelte'
  import SavedLayouts from './components/SavedLayouts.svelte'
  import PresetLayouts from './components/PresetLayouts.svelte'
  import Button from '@/components/core/ui/buttons/v2.0.0/Button.svelte'
  import Modal from '@/components/core/extensions/modal/v2.0.0/Modal'

  export let sidebarModes = []

  const platform = getContext('platform')
  const { store } = platform
  const { node } = $store.editor.editingSection

  const presetLayouts = platform.components[$store.render.template.custom.ref]?.presets

  const tabs = {
    PRESET: {
      name: 'Preset',
      value: 'preset',
      hidden: !presetLayouts,
    },
    BLANK: {
      name: 'Blank',
      value: 'blank',
    },
    SAVED: {
      name: 'Saved',
      value: 'saved',
    },
  }

  let currentTab
  let selectedContentBlock
  let syncStyles = 'all' // all | duplicate | blank
  let syncPlugins = 'all' // all | duplicate | blank

  function openContentBlockPrompt (contentBlock) {
    selectedContentBlock = contentBlock
    platform.api.browser.modal('cms-content-blocks-prompt').open()
  }

  function promptContentBlockCancel () {
    platform.api.browser.modal('cms-content-blocks-prompt').close()
    selectedContentBlock = null
  }

  function promptContentBlockConfirm () {
    addContentBlockAndPlugins(selectedContentBlock)

    platform.api.browser.modal('cms-content-blocks-prompt').close()
    selectedContentBlock = null
  }

  function addContentBlockAndPlugins (savedBlock) {
    const layoutKey = addContentBlock(savedBlock.layout)

    setTimeout(async () => {
      let index = 0
      for (const plugin of savedBlock.plugins) {
        await addPlugin(plugin, layoutKey, index)
        index += 1
      }
    }, 0)
  }

  function addContentBlock (block) {
    if (!node.fields.layouts) {
      node.fields.layouts = []
    }

    const nextKey = (Math.max(0, ...node.fields.layouts.map(l => Number.isNaN(Number(l.key)) ? 0 : Number(l.key))) + 1).toString()

    node.fields.layouts.splice($store.editor.currentIndex, 0, {
      key: nextKey,
      layout: block.name,
    })

    if (block?.fields && syncStyles !== 'blank') {
      for (const [key, value] of Object.entries(block.fields)) {
        node.fields.layouts[$store.editor.currentIndex][key] = value
      }
    }

    if (node.type === platform.api.core.template.COL_UNIQUE) {
      $store.render.nodeFields[node.id] = $store.render.nodeFields[node.id] || {}
      $store.render.nodeFields[node.id].layouts = node.fields.layouts
    }

    if (syncStyles !== 'all' || !node.fields.layouts[$store.editor.currentIndex].styleSyncID) {
      node.fields.layouts[$store.editor.currentIndex].styleSyncID = platform.api.core.uuid()
    }

    if (!$store.render.styleSyncIDs[node.fields.layouts[$store.editor.currentIndex].styleSyncID]) {
      const styles = {
        id: '',
        bucketID: '',
        bucketName: '',
        payload: {},
      }

      if (syncStyles !== 'blank') {
        if (block?.fields?.styleSyncID && $store.render.styleSyncIDs[block.fields.styleSyncID]?.payload) {
          const payload = $store.render.styleSyncIDs[block.fields.styleSyncID]?.payload
          styles.payload = JSON.parse(JSON.stringify(payload ?? {}))
        } else {
          styles.payload = JSON.parse(JSON.stringify(block?.fields ?? {}))
        }
      }

      $store.render.styleSyncIDs[node.fields.layouts[$store.editor.currentIndex].styleSyncID] = styles
    }

    $store.editor.actions.push({
      name: 'NODE_SET_LAYOUTS',
      meta: {
        layouts: node.fields.layouts,
        nodeId: node.id,
        source: 'v1.0.0/ContentBlocks.svelte: addContentBlock',
      },
    })

    $store.render = $store.render

    handleClose()

    return nextKey
  }

  async function addPlugin (plugin, layoutKey, index) {
    const pluginRef = plugin.ref
    const pluginFields = JSON.parse(plugin.fields)
    const nodeName = plugin.nodeName
    const pluginName = plugin.pluginName || ''
    const pluginID = plugin.pluginID || ''

    const node = platform.api.core.template?.findNode($store.render, node => {
      if (layoutKey) {
        return node.fields?.layoutKey === layoutKey && node.fields?.name === nodeName
      }

      return node.fields?.name === nodeName
    })

    if (!node) {
      if (layoutKey) {
        throw new Error(`Node "${nodeName}" (layout key "${layoutKey}") not found.`)
      }

      throw new Error(`Node "${nodeName}" not found.`)
    }

    const nodeID = platform.api.core.uuid()
    const blockID = platform.api.core.template.childBlockId($store.render, node)

    if (!blockID) {
      throw new Error(`Block not found.`)
    }

    /*
      Order of attempted operations:
      1. Attempt to use PluginID if passed in and sync is enabled
      2. If sync is disabled, try and clone the fields by PluginID and create a new Plugin
      3. If the PluginID is not present, and PluginName is, attempt to fetch the plugin by name
      4. Finally, just create a new plugin from the existing cloned fields

      Note to self:
      At some point I must drop PluginNames, it was a mistake to use names to keep track of named
      plugins, as it requires a separate request, and is very fragile...
      So, pluginIDs will be the only way to sync plugin state, which is ok, but could break websites
      such as Consultus :(
    */
    if (syncPlugins === 'all') {
      if (pluginID) {
        try {
          const plugin = await platform.api.cms.plugins.find({
            where: `plugin.id = ?`,
            whereArgs: [pluginID],
          })

          if (!plugin.name) {
            const updatedPlugin = platform.api.core.copyObject(plugin)
            updatedPlugin.name = '__jolojo_generated__' + platform.api.core.uuid()
            await platform.api.cms.plugins.update(updatedPlugin)
          }

          $store.editor.actions.push({
            name: 'BLOCK_ADD_PLUGIN_NODE',
            meta: {
              blockId: blockID,
              nodeId: nodeID,
              pluginId: pluginID,
              index,
            },
          })

          $store.render = $store.render
          $store.editor = $store.editor
        } catch (err) {
          console.error('Possibly missing:', err)
        }

        return
      }

      if (pluginName) {
        let foundPluginID
        let foundPluginFields

        try {
          const plugin = await platform.api.cms.plugins.find({
            where: `plugin.name = ?`,
            whereArgs: [pluginName],
          })

          foundPluginID = plugin.id
          foundPluginFields = plugin.fields
        } catch (err) {
          console.error('Possibly missing:', err)

          return
        }

        $store.render.blocks[blockID].nodes.splice(index, 0, {
          id: nodeID,
          blockId: blockID,
          pluginId: foundPluginID,
          type: 'plugin',
          fields: foundPluginFields,
          sortIndex: index,
        })

        $store.editor.actions.push({
          name: 'BLOCK_ADD_PLUGIN_NODE',
          meta: {
            blockId: blockID,
            nodeId: nodeID,
            pluginId: foundPluginID,
            index,
          },
        })

        $store.render = $store.render
        $store.editor = $store.editor

        return
      }

      { // Last resort, use cloned fields
        const newPluginID = platform.api.core.uuid()
        const newPluginFields = pluginFields
        const newPluginName = '__jolojo_generated__' + platform.api.core.uuid()

        $store.render.plugins[newPluginID] = {
          id: newPluginID,
          ref: pluginRef,
          fields: newPluginFields,
          name: newPluginName,
        }

        $store.editor.actions.push({
          name: 'PLUGIN_CREATE',
          meta: {
            pluginId: newPluginID,
            ref: pluginRef,
            fields: newPluginFields,
            name: newPluginName,
            isExisting: false,
          },
        })

        if (!$store.render.blocks[blockID]) {
          $store.render.blocks[blockID] = { nodes: [] }
        }

        $store.render.blocks[blockID].nodes = $store.render?.blocks[blockID]?.nodes || []

        $store.render.blocks[blockID].nodes.splice(index, 0, {
          id: nodeID,
          blockId: blockID,
          pluginId: newPluginID,
          type: 'plugin',
          fields: newPluginFields,
          sortIndex: index,
        })

        $store.editor.actions.push({
          name: 'BLOCK_ADD_PLUGIN_NODE',
          meta: {
            blockId: blockID,
            nodeId: nodeID,
            pluginId: newPluginID,
            index,
          },
        })

        $store.render = $store.render
        $store.editor = $store.editor
      }
    } else {
      const newPluginID = platform.api.core.uuid()
      let newPluginFields = {} // Default for sync === 'blank'

      if (syncPlugins === 'duplicate') {
        newPluginFields = pluginFields

        if (pluginID) {
          try {
            const plugin = await platform.api.cms.plugins.find({
              where: `plugin.id = ?`,
              whereArgs: [pluginID],
            })

            newPluginFields = plugin.fields
          } catch (err) {
            console.error('Possibly missing:', err)
          }
        }
        if (pluginName) {
          try {
            const plugin = await platform.api.cms.plugins.find({
              where: `plugin.name = ?`,
              whereArgs: [pluginName],
            })

            newPluginFields = plugin.fields
          } catch (err) {
            console.error('Possibly missing:', err)
          }
        }
      }

      $store.render.plugins[newPluginID] = {
        id: newPluginID,
        ref: pluginRef,
        fields: newPluginFields,
        name: pluginName,
      }

      $store.editor.actions.push({
        name: 'PLUGIN_CREATE',
        meta: {
          pluginId: newPluginID,
          name: pluginName,
          ref: pluginRef,
          fields: newPluginFields,
          isExisting: false,
        },
      })

      if (!$store.render.blocks[blockID]) {
        $store.render.blocks[blockID] = { nodes: [] }
      }

      $store.render.blocks[blockID].nodes = $store.render?.blocks[blockID]?.nodes || []

      $store.render.blocks[blockID].nodes.splice(index, 0, {
        id: nodeID,
        blockId: blockID,
        pluginId: newPluginID,
        type: 'plugin',
        fields: newPluginFields,
        sortIndex: index,
      })

      $store.editor.actions.push({
        name: 'BLOCK_ADD_PLUGIN_NODE',
        meta: {
          blockId: blockID,
          nodeId: nodeID,
          pluginId: newPluginID,
          index,
        },
      })

      $store.render = $store.render
      $store.editor = $store.editor
    }
  }

  function handleClose () {
    $store.sidebar.mode = sidebarModes.CATEGORIES
    $store.editor.currentIndex = null
  }

  async function setSyncStylesAndPage (page) {
    syncStyles = page ?? 'all'
    syncPlugins = page ?? 'all'
  }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->

<Modal
  title="Sync content block"
  id="cms-content-blocks-prompt"
  on:cancelClose={promptContentBlockCancel}
>
  <div slot="body">
    <div class="warning-banner">
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M3 12c0 2.21.91 4.2 2.36 5.64L3 20h6v-6l-2.24 2.24C5.68 15.15 5 13.66 5 12c0-2.61 1.67-4.83 4-5.65V4.26C5.55 5.15 3 8.27 3 12zm8 5h2v-2h-2v2zM21 4h-6v6l2.24-2.24C18.32 8.85 19 10.34 19 12c0 2.61-1.67 4.83-4 5.65v2.09c3.45-.89 6-4.01 6-7.74 0-2.21-.91-4.2-2.36-5.64L21 4zm-10 9h2V7h-2v6z"/></svg>
      <span>
        By syncing, this content block will pick up any changes made to other synced content blocks - and changes made
        to this content block will also affect other synced content blocks
        <br>
        <br>
        To stop this behavior in the future, you will need to delete and re-add the content block
      </span>
    </div>

    <table class="sync-options-table">
      <thead>
        <tr>
          <td>
            <h2>Sync options</h2>
          </td>
          <td>
            <Button type='dark' on:click={() => { setSyncStylesAndPage('all') }}>Sync</Button>
          </td>
          <td>
            <Button type='dark' on:click={() => { setSyncStylesAndPage('duplicate') }}>Duplicate</Button>
          </td>
          <td>
            <Button type='dark' on:click={() => { setSyncStylesAndPage('blank') }}>Blank</Button>
          </td>
        </tr>
      </thead>
      <tr>
        <td>Styles</td>
        <td>
          <Button type={syncStyles === 'all' ? 'secondary' : 'light'} on:click={() => { syncStyles = 'all' }}>Sync</Button>
        </td>
        <td>
          <Button type={syncStyles === 'duplicate' ? 'secondary' : 'light'} on:click={() => { syncStyles = 'duplicate' }}>Duplicate</Button>
        </td>
        <td>
          <Button type={syncStyles === 'blank' ? 'secondary' : 'light'} on:click={() => { syncStyles = 'blank' }}>Blank</Button>
        </td>
      </tr>
      <tr>
        <td>Plugins</td>
        <td>
          <Button type={syncPlugins === 'all' ? 'secondary' : 'light'} on:click={() => { syncPlugins = 'all' }}>Sync</Button>
        </td>
        <td>
          <Button type={syncPlugins === 'duplicate' ? 'secondary' : 'light'} on:click={() => { syncPlugins = 'duplicate' }}>Duplicate</Button>
        </td>
        <td>
          <Button type={syncPlugins === 'blank' ? 'secondary' : 'light'} on:click={() => { syncPlugins = 'blank' }}>Blank</Button>
        </td>
      </tr>
    </table>
  </div>
  <div slot="toggles">
    <Button on:click={promptContentBlockConfirm} type="primary">Confirm settings</Button>
  </div>
</Modal>

<div
  class="jolojo-content-blocks"
  transition:fly={{
    x: -200,
    duration: 500,
  }}
  on:click={handleClose}
>
  <div
    class="jolojo-content-blocks__container"
    on:click|stopPropagation
  >
    <div class="jolojo-content-blocks__header">
      <h2>Content Blocks</h2>
      <Button icon='close' type='secondary' rounded on:click={handleClose}/>
    </div>
    <Tabs {tabs} bind:value={currentTab} />

    {#if currentTab === tabs.PRESET.value}
      <PresetLayouts {platform} addBlock={openContentBlockPrompt}/>
    {/if}

    {#if currentTab === tabs.BLANK.value}
      <BlankLayouts {platform} addBlock={addContentBlock}/>
    {/if}

    {#if currentTab === tabs.SAVED.value}
      <SavedLayouts {platform} addBlock={openContentBlockPrompt}/>
    {/if}
  </div>
</div>

<style lang="scss">.jolojo-content-blocks {
  position: fixed;
  left: 0;
  top: 0;
  height: 100dvh;
  width: 100%;
  z-index: 400;
}
.jolojo-content-blocks h2 {
  font-weight: bold;
  font-size: 1.25rem;
}
.jolojo-content-blocks__container {
  padding: 1.5rem;
  padding-left: calc(50px + 1.5rem);
  padding-top: calc(50px + 1.5rem);
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  height: 100%;
  max-width: 1000px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.jolojo-content-blocks__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.warning-banner {
  margin: 0 0 16px 0;
  padding: 8px 10px 8px 44px;
  border-radius: 8px;
  background: rgba(255, 167, 51, 0.1333333333);
  color: black;
  border: 1px solid #ffa733;
  position: relative;
}
.warning-banner svg {
  position: absolute;
  top: 8px;
  left: 10px;
  color: #ffa733;
}

.sync-options-table td {
  padding: 0.5rem;
}
.sync-options-table thead h2 {
  font-weight: bold;
  margin: 0;
}</style>
<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};</script>